import React, { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import DashboardLayout from "../../components/DashboardLayout";
import FleetrifyCard from "../../components/Card";
import FleetrifyFilter from "../../components/Filter/filter";
import { useOrdersControllers } from "../../controllers/useOrdersControllers";
import OrdersTable from "../../components/Table/OrdersTable";
import { DashboardFleetSummaryCard } from "../../components/pages/Dashboard/Cards/summary";
// import { usePermission } from "../../hooks/usePermission";
import { useGetAllOrdersQuery, useGetCompanyOrderStatsQuery, useGetDispatchOrdersQuery } from "../../redux/services/orders";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/services/root-reducer";
import { showNegotiationBadge } from "../../components/NegotiationRequest";

interface OrderTableData {
  // id: number;
  id: string;
  orderId: string;
  customer: string;
  serviceType: string;
  pickUpLocation: string;
  deliveryLocation: string;
   price: string;
  driver: string | null;
  status: string;
  date: string;
  paymentStatus: string | null;
  paymentPreference: string | null;
  paymentMethod: string | null;
  cashCollected: boolean | null;
  customerConfirmedCashAt: string | null;
  negotiationRequested: boolean;
}


const orderStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Counter Sent", value: "counter_sent" },
  { label: "Customer Countered", value: "customer_countered" },
  { label: "In Progress", value: "in_progress" },
  { label: "Accepted", value: "accepted" },
  { label: "Picked Up", value: "picked_up" },
  { label: "In Transit", value: "in_transit" },
  { label: "Out for Delivery", value: "out_for_delivery" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Rejected", value: "rejected" },
];

// Day-to-day statuses from /dispatch-orders `counts`. The first two are waiting on the company.
const dispatchCountTabs = [
  { label: "Pending", value: "pending", needsAction: true },
  { label: "Customer countered", value: "customer_countered", needsAction: true },
  { label: "Counter sent", value: "counter_sent", needsAction: false },
  { label: "Accepted", value: "accepted", needsAction: false },
  { label: "Rejected", value: "rejected", needsAction: false },
];

const serviceTypeOptions = [
  { label: "Local Relocation", value: "local_relocation" },
  { label: "Day-to-day Delivery", value: "local_dispatch" },
  { label: "State Relocation", value: "interstate_relocation" },
];

const Orders = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const companyId = user?.company?.id ?? "";
  const {
    filter,
    handleFilterChange,
    handleClearFilters,
    dateRange,
    handleDateRangeChange,
  } = useOrdersControllers();
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch] = useDebounce(filter.search, 500);

  const { data: stats } = useGetCompanyOrderStatsQuery({ companyId });
  const { data: dispatchData } = useGetDispatchOrdersQuery({ per_page: 1 });
  const dispatchCounts: Record<string, number> = dispatchData?.data?.counts ?? {};
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useGetAllOrdersQuery({
    page: currentPage,
    per_page: 5,
    search: debouncedSearch,
    status: filter.status,
    order_type: filter.order_type,
  });

  const ordersLastPage = ordersData?.data?.meta?.last_page ?? 1;
  const ordersPageSize = ordersData?.data?.meta?.per_page ?? 5;
  const ordersTotal = ordersData?.data?.meta?.total;

  const { search, status, order_type } = filter;

  const handleFilterChangeWithReset = (key: keyof typeof filter, value: string) => {
    handleFilterChange(key, value);
    setCurrentPage(1);
  };

  const handleFilterClick = () => {
    setCurrentPage(1);
  };

  const showDispatchStatus = (value: string) => {
    handleFilterChange("order_type", "local_dispatch");
    handleFilterChange("status", value);
    setCurrentPage(1);
  };

  const handleClearFiltersAndReset = () => {
    handleClearFilters();
    setCurrentPage(1);
  };
  // const { hasPermission } = usePermission();

  // Derived straight from the query result — no need for its own state
  // (and the extra render that setting it from an effect would cost).
  const orders = useMemo<OrderTableData[]>(() => {
    if (!ordersData?.data?.data) return [];

    return ordersData.data.data.map((order: any) => ({
      id: order.id || `#${Math.floor(10000 + Math.random() * 90000)}`,
      orderId: order.order_number || `#${Math.floor(10000 + Math.random() * 90000)}`,
      customer: order.customer?.name || "Unknown",
      serviceType: (order.order_type).replace(/_/g, " ").replace(/\b\w/g, (char: string) => char.toUpperCase()) || "Unknown",
      pickUpLocation: order.pickup_location || "Unknown",
      deliveryLocation: order.delivery_location || "Unknown",
      price: order.price ?? "0",
      driver: order.driver?.name ?? null,
      status: order.status.replace(/_/g, " ").replace(/\b\w/g, (char: string) => char.toUpperCase()),
      date: new Date(order.created_at || Date.now()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      paymentStatus: order.payment_status ?? null,
      paymentPreference: order.payment_preference ?? null,
      paymentMethod: order.payment_method ?? null,
      cashCollected: order.cash_collected ?? null,
      customerConfirmedCashAt: order.customer_confirmed_cash_at ?? null,
      negotiationRequested: showNegotiationBadge(order),
    }));
  }, [ordersData]);

  return (
    <DashboardLayout
      title="Orders"
      description="Get a clear, actionable view of your staff and operations in one place. Track key metrics, and export the data you need to make faster, smarter decisions every day."
      containerClass="space-y-10"
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      showDateRange={true}
      onDataRefresh={() => console.log("Data refreshed")}
    >
      {ordersLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Orders data...</p>
          </div>
        </div>
      ) : (
      <main className="space-y-10">
        {/* summary card group */}
        <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
          <h3 className="text-sm font-semibold text-[#00032A] mb-4">Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <DashboardFleetSummaryCard
              title="Total orders"
              value={stats?.data?.total?.count ?? 0}
              changeText={`${stats?.data.total?.trend_percentage ?? 0}% from previous month`}
              changeDirection={stats?.data.total?.trend === "increase" ? "up" : stats?.data.total?.trend === "decrease" ? "down" : "mid"}
            />
            <DashboardFleetSummaryCard
              title="Total pending orders"
              value={stats?.data?.pending?.count ?? 0}
              changeText={`${stats?.data.pending?.trend_percentage ?? 0}% from last quarter`}
              changeDirection={stats?.data.pending?.trend === "increase" ? "up" : stats?.data.pending?.trend === "decrease" ? "down" : "mid"}
            />
            <DashboardFleetSummaryCard
              title="Total order In Transit"
              value={stats?.data?.in_transit?.count ?? 0}
              changeText={`${stats?.data.in_transit?.trend_percentage ?? 0}% from last quarter`}
              changeDirection={stats?.data.in_transit?.trend === "increase" ? "up" : stats?.data.in_transit?.trend === "decrease" ? "down" : "mid"}
            />
            <DashboardFleetSummaryCard
              title="Delivered orders"
              value={stats?.data?.completed?.count ?? 0}
              changeText={`${stats?.data.completed?.trend_percentage ?? 0}% from last quarter`}
              changeDirection={stats?.data.completed?.trend === "increase" ? "up" : stats?.data.completed?.trend === "decrease" ? "down" : "mid"}
            />
            <DashboardFleetSummaryCard
              title="Canceled orders"
              value={stats?.data?.cancelled?.count ?? 0}
              changeText={`${stats?.data.cancelled?.trend_percentage ?? 0}% from last quarter`}
              changeDirection={stats?.data.cancelled?.trend === "increase" ? "up" : stats?.data.cancelled?.trend === "decrease" ? "down" : "mid"}
            />
          </div>
        </section>
        {dispatchData?.data?.counts && (
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[#00032A]">Day-to-day delivery offers</h3>
              <span className="text-xs text-gray-400">{dispatchCounts.total ?? 0} total</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {dispatchCountTabs.map((tab) => {
                const count = dispatchCounts[tab.value] ?? 0;
                const isActive = order_type === "local_dispatch" && status === tab.value;
                const urgent = tab.needsAction && count > 0;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => showDispatchStatus(tab.value)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      isActive
                        ? "border-[#143887] bg-[#143887] text-white"
                        : urgent
                        ? "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {urgent && !isActive && <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-500" />}
                    {tab.label}
                    <span className={`rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums ${isActive ? "bg-white/20" : "bg-gray-100 text-gray-700"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        <FleetrifyCard cardContainerClass="w-full  !pt-0">
          <div className="relative">
            <OrdersTable
              tableData={orders}
              currentPage={currentPage}
              lastPage={ordersLastPage}
              pageSize={ordersPageSize}
              total={ordersTotal}
              setCurrentPage={setCurrentPage}
              isLoading={ordersFetching}
              renderLeftContent={
                <FleetrifyFilter
                  searchInputClass="!bg-[#F5F6FA]"
                  selectWrapClass="!bg-[#F5F6FA]"
                  searchPlaceholder="Search order ID or customer name"
                  searchValue={search}
                  onSearchChange={(value) => handleFilterChangeWithReset("search", value)}
                  serviceOption={serviceTypeOptions}
                  serviceValue={order_type}
                  onServiceChange={(value: any) =>
                    handleFilterChangeWithReset("order_type", value)
                  }
                  statusOption={orderStatusOptions}
                  statusValue={status}
                  onStatusChange={(value: any) =>
                    handleFilterChangeWithReset("status", value)
                  }
                  onFilterClick={handleFilterClick}
                  onClearFilter={handleClearFiltersAndReset}
                />
              }
            />
          </div>
        </FleetrifyCard>
      </main>
    )}
    </DashboardLayout>
  );
};

export default Orders;
